/**
 * RBAC permission catalog and helpers for the admin dashboard.
 *
 * The backend (`backend/src/middleware/permission.middleware.js`) is the single
 * source of truth and returns the resolved `permissions[]` for the logged-in
 * admin on `/auth/admin-login` and `/auth/me`. This module mirrors that matrix
 * so the UI can:
 *   1. gate navigation and actions exactly the way the API gates routes, and
 *   2. still derive permissions from `admin_role` when only a token is present
 *      (e.g. before `/auth/me` resolves) so navigation never flickers.
 *
 * Keep ROLE_PERMISSIONS in sync with the backend. A parity test asserts this.
 */

export type AdminRole = 'owner' | 'manager' | 'operator' | 'accountant'

export type Permission =
  | 'orders.view'
  | 'orders.create'
  | 'orders.update'
  | 'orders.assign'
  | 'orders.delete'
  | 'agents.view'
  | 'agents.create'
  | 'agents.suspend'
  | 'agents.payout'
  | 'customers.view'
  | 'customers.create'
  | 'customers.update'
  | 'customers.suspend'
  | 'customers.loyalty'
  | 'partners.view'
  | 'partners.create'
  | 'partners.score'
  | 'complaints.view'
  | 'complaints.update'
  | 'cash.view'
  | 'cash.deposit'
  | 'cash.bulk_deposit'
  | 'analytics.view'
  | 'broadcast.send'
  | 'referrals.credit'
  // Wallet & referrals. Split three ways: reading the ledger, moving a
  // customer's balance, and deciding what a campaign gives away are three
  // different levels of trust.
  | 'wallet.view'
  | 'wallet.adjust'
  | 'wallet.manage'
  | 'coupons.create'
  | 'coupons.update'
  | 'coupons.delete'
  | 'services.update'
  | 'expenses.manage'
  | 'settings.update'
  | 'marketing.view'
  | 'notifications.view'
  | 'notifications.create'
  | 'notifications.approve'
  | 'notifications.send'
  // B2B bulk billing. view = read; bill = enter a room's laundry (the daily
  // operator job); manage = onboard clients, change negotiated rates, and
  // generate the invoice that closes a cycle.
  | 'b2b.view'
  | 'b2b.bill'
  | 'b2b.manage'

// Mirror of backend ROLE_PERMISSIONS. Owner uses the '*' wildcard.
export const ROLE_PERMISSIONS: Record<AdminRole, Permission[] | ['*']> = {
  owner: ['*'],
  manager: [
    'orders.view',
    'orders.create',
    'orders.update',
    'orders.assign',
    'orders.delete',
    'agents.view',
    'agents.create',
    'agents.suspend',
    'agents.payout',
    'customers.view',
    'customers.create',
    'customers.update',
    'customers.suspend',
    'customers.loyalty',
    'partners.view',
    'partners.create',
    'partners.score',
    'complaints.view',
    'complaints.update',
    'cash.view',
    'cash.deposit',
    'cash.bulk_deposit',
    'analytics.view',
    'broadcast.send',
    'referrals.credit',
    // Wallet: read the ledger, adjust a balance by hand, and decide what
    // top-up offers and referral campaigns give away.
    'wallet.view',
    'wallet.adjust',
    'wallet.manage',
    'coupons.create',
    'coupons.update',
    'coupons.delete',
    'services.update',
    'expenses.manage',
    'marketing.view',
    'notifications.view',
    'notifications.create',
    'notifications.approve',
    'notifications.send',
    'b2b.view',
    'b2b.bill',
    'b2b.manage',
  ],
  operator: [
    'orders.view',
    'agents.view',
    'customers.view',
    'complaints.view',
    'notifications.view',
    // The counter operator bills rooms but cannot onboard a client, change a
    // rate, or generate the invoice that closes the cycle.
    'b2b.view',
    'b2b.bill',
  ],
  accountant: [
    'cash.view',
    'cash.deposit',
    'cash.bulk_deposit',
    'agents.payout',
    'expenses.manage',
    'analytics.view',
    // The wallet is a liability on the books, so reconciling it is the
    // accountant's job - reading only; they may not move a balance.
    'wallet.view',
    // Read-only: chases payment, does not enter billing.
    'b2b.view',
  ],
}

// Note: 'settings.update' is owner-only on the backend (owner wildcard). It is
// intentionally NOT in any non-owner role list above.
export const ALL_PERMISSIONS: Permission[] = Array.from(
  new Set(
    (Object.values(ROLE_PERMISSIONS).flat() as string[]).filter(
      (perm): perm is Permission => perm !== '*',
    ),
  ),
)
// settings.update is owner-only and not present in any listed role; ensure it is
// part of the wildcard expansion so owners can do it.
if (!ALL_PERMISSIONS.includes('settings.update')) {
  ALL_PERMISSIONS.push('settings.update')
}

/** Resolve concrete permissions for a role (expands the owner wildcard). */
export function resolvePermissionsForRole(role: string | undefined): Permission[] {
  if (!role) return []
  const list = ROLE_PERMISSIONS[role as AdminRole]
  if (!list) return []
  if ((list as string[]).includes('*')) return [...ALL_PERMISSIONS]
  return [...(list as Permission[])]
}

interface PermissionedUser {
  role?: string
  admin_role?: string
  permissions?: string[]
}

/**
 * Does this user hold a permission?
 * Prefers the server-provided `permissions[]`; falls back to deriving from
 * `admin_role`. A wildcard '*' in the list grants everything.
 */
export function userHasPermission(
  user: PermissionedUser | null | undefined,
  permission: Permission,
): boolean {
  if (!user) return false
  if (user.role && user.role !== 'admin') return false

  const granted =
    user.permissions && user.permissions.length > 0
      ? user.permissions
      : resolvePermissionsForRole(user.admin_role)

  return granted.includes('*') || granted.includes(permission)
}
